import { pdfjs } from 'react-pdf';
import { CAPTURE_WEBP_QUALITY, captureCanvasSize } from './captureRegion';

const PDF_THUMBNAIL_WIDTH_PX = 280;
const PDF_PAGE_CANVAS_CLASS = 'react-pdf__Page__canvas';
const PDF_REGION_CAPTURE_TIMEOUT_MS = 15_000;

export const PDF_CAPTURE_URL_ATTR = 'data-pdf-capture-url';
export const PDF_CAPTURE_ROTATION_ATTR = 'data-pdf-capture-rotation';

const pdfjsCaptureOptions = {
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

function ensurePdfWorker(): void {
  if (typeof window === 'undefined') return;
  if (pdfjs.GlobalWorkerOptions.workerSrc) return;
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();
}

/**
 * Capture the first ``react-pdf`` page canvas inside ``container`` as a
 * WebP data URL. Shared by the fullscreen bookmark button and the
 * offscreen auto-thumbnail host so the two paths cannot drift.
 */
export function capturePdfCanvasThumbnail(
  container: HTMLElement | null | undefined,
): string | null {
  if (!container) return null;
  const canvas = container.getElementsByClassName(
    PDF_PAGE_CANVAS_CLASS,
  )[0] as HTMLCanvasElement | undefined;
  if (!canvas) return null;

  const canvasWidth = canvas.clientWidth || PDF_THUMBNAIL_WIDTH_PX;
  const canvasHeight = canvas.clientHeight || 120;
  const canvasAspectRatio = canvasWidth / canvasHeight;
  const canvasCopy = document.createElement('canvas');
  canvasCopy.width = PDF_THUMBNAIL_WIDTH_PX;
  canvasCopy.height = canvasCopy.width / canvasAspectRatio;
  const ctx = canvasCopy.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, 0, 0, canvasCopy.width, canvasCopy.height);
  return canvasCopy.toDataURL('image/webp', CAPTURE_WEBP_QUALITY);
}

export type PdfRegionCapture = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function findPdfPageCanvases(root: HTMLElement): HTMLCanvasElement[] {
  const byClass = Array.from(
    root.getElementsByClassName(PDF_PAGE_CANVAS_CLASS),
  ) as HTMLCanvasElement[];
  if (byClass.length > 0) return byClass;
  return Array.from(root.querySelectorAll('.react-pdf__Page canvas')) as HTMLCanvasElement[];
}

export function isPdfCaptureRoot(root: HTMLElement): boolean {
  return Boolean(
    root.querySelector(
      `[${PDF_CAPTURE_URL_ATTR}], .${PDF_PAGE_CANVAS_CLASS}, .react-pdf__Page`,
    ),
  );
}

function canvasExportIsReadable(canvas: HTMLCanvasElement): boolean {
  try {
    canvas.toDataURL('image/webp');
    return true;
  } catch {
    return false;
  }
}

/** ``canvas`` when the crop succeeded, otherwise why it did not. */
type CropOutcome = { canvas: HTMLCanvasElement | null; reason?: string };

function cropOutcome(
  pageCanvas: HTMLCanvasElement,
  pageBox: DOMRect,
  root: HTMLElement,
  cap: PdfRegionCapture,
): CropOutcome {
  if (cap.width <= 0 || cap.height <= 0) {
    return { canvas: null, reason: 'empty-selection' };
  }
  if (pageBox.width <= 0 || pageBox.height <= 0) {
    return { canvas: null, reason: 'page-box-empty' };
  }
  if (pageCanvas.width <= 0 || pageCanvas.height <= 0) {
    return { canvas: null, reason: 'page-bitmap-empty' };
  }

  const dest = document.createElement('canvas');
  const scaleX = pageCanvas.width / pageBox.width;
  const scaleY = pageCanvas.height / pageBox.height;
  const destSize = captureCanvasSize(cap.width, cap.height, scaleX, scaleY);
  dest.width = destSize.width;
  dest.height = destSize.height;
  const context = dest.getContext('2d');
  if (!context) return { canvas: null, reason: 'no-2d-context' };
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, dest.width, dest.height);

  const rootRect = root.getBoundingClientRect();
  const selectionLeft = rootRect.left + cap.x;
  const selectionTop = rootRect.top + cap.y;
  const selectionRight = selectionLeft + cap.width;
  const selectionBottom = selectionTop + cap.height;

  const intersectLeft = Math.max(selectionLeft, pageBox.left);
  const intersectTop = Math.max(selectionTop, pageBox.top);
  const intersectRight = Math.min(selectionRight, pageBox.right);
  const intersectBottom = Math.min(selectionBottom, pageBox.bottom);
  const destWidth = intersectRight - intersectLeft;
  const destHeight = intersectBottom - intersectTop;
  if (destWidth <= 0 || destHeight <= 0) {
    return { canvas: null, reason: 'no-intersection' };
  }

  const destScaleX = cap.width > 0 ? dest.width / cap.width : 1;
  const destScaleY = cap.height > 0 ? dest.height / cap.height : 1;
  try {
    context.drawImage(
      pageCanvas,
      (intersectLeft - pageBox.left) * scaleX,
      (intersectTop - pageBox.top) * scaleY,
      destWidth * scaleX,
      destHeight * scaleY,
      (intersectLeft - selectionLeft) * destScaleX,
      (intersectTop - selectionTop) * destScaleY,
      destWidth * destScaleX,
      destHeight * destScaleY,
    );
  } catch (error) {
    return {
      canvas: null,
      reason: `draw-failed: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
  if (!canvasExportIsReadable(dest)) {
    return { canvas: null, reason: 'tainted' };
  }
  return { canvas: dest };
}

/**
 * Crop ``pageCanvas`` into a new canvas covering ``cap`` in ``root``
 * coordinates, using ``pageBox`` (typically the on-screen page canvas
 * ``getBoundingClientRect``) as the CSS mapping.
 */
export function cropPageCanvasToRootSelection(
  pageCanvas: HTMLCanvasElement,
  pageBox: DOMRect,
  root: HTMLElement,
  cap: PdfRegionCapture,
): HTMLCanvasElement | null {
  return cropOutcome(pageCanvas, pageBox, root, cap).canvas;
}

/**
 * Crop the on-screen ``react-pdf`` page canvas(es) to a selection in
 * ``root`` coordinates. html2canvas cannot clone pdf.js canvases (tainted /
 * 0×0 clone), so New Thumbnail must blit them directly when the display
 * canvas is readable.
 */
function blitPdfRegion(root: HTMLElement, cap: PdfRegionCapture): CropOutcome {
  if (cap.width <= 0 || cap.height <= 0) {
    return { canvas: null, reason: 'empty-selection' };
  }
  const canvases = findPdfPageCanvases(root);
  if (canvases.length === 0) {
    return { canvas: null, reason: 'no-page-canvas' };
  }
  const reasons: string[] = [];
  for (const canvas of canvases) {
    const outcome = cropOutcome(canvas, canvas.getBoundingClientRect(), root, cap);
    if (outcome.canvas) return outcome;
    reasons.push(outcome.reason ?? 'unknown');
  }
  return { canvas: null, reason: reasons.join(', ') };
}

export function capturePdfRegionCanvas(
  root: HTMLElement,
  cap: PdfRegionCapture,
): HTMLCanvasElement | null {
  return blitPdfRegion(root, cap).canvas;
}

function parseCaptureRotation(host: Element | null): number {
  const raw = host?.getAttribute(PDF_CAPTURE_ROTATION_ATTR);
  const value = raw ? Number(raw) : 0;
  if (!Number.isFinite(value)) return 0;
  return ((value % 360) + 360) % 360;
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

async function renderPdfPageCanvas(args: {
  fileUrl: string;
  rotation: number;
  targetWidth: number;
}): Promise<CropOutcome> {
  ensurePdfWorker();
  const { fileUrl, rotation, targetWidth } = args;
  if (targetWidth <= 0) return { canvas: null, reason: 'render-target-empty' };

  let pdf: Awaited<ReturnType<typeof pdfjs.getDocument>['promise']> | undefined;
  try {
    const data = await (await fetch(fileUrl)).arrayBuffer();
    pdf = await pdfjs.getDocument({ data, ...pdfjsCaptureOptions }).promise;
    const page = await pdf.getPage(1);
    const unscaled = page.getViewport({ scale: 1, rotation });
    if (unscaled.width <= 0) return { canvas: null, reason: 'render-viewport-empty' };
    const scale = targetWidth / unscaled.width;
    const viewport = page.getViewport({ scale, rotation });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const context = canvas.getContext('2d');
    if (!context) return { canvas: null, reason: 'render-no-2d-context' };
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;
    if (!canvasExportIsReadable(canvas)) {
      return { canvas: null, reason: 'render-tainted' };
    }
    return { canvas };
  } catch (error) {
    return {
      canvas: null,
      reason: `render-failed: ${error instanceof Error ? error.message : String(error)}`,
    };
  } finally {
    void pdf?.destroy();
  }
}

function findPdfPageDisplay(root: HTMLElement): {
  canvas?: HTMLCanvasElement;
  box: DOMRect;
} | null {
  for (const canvas of findPdfPageCanvases(root)) {
    const box = canvas.getBoundingClientRect();
    if (box.width > 0 && box.height > 0) {
      return { canvas, box };
    }
  }
  const page = root.querySelector('.react-pdf__Page') as HTMLElement | null;
  if (!page) return null;
  const box = page.getBoundingClientRect();
  if (box.width <= 0 || box.height <= 0) return null;
  return { box };
}

export type PdfRegionCaptureResult = {
  canvas: HTMLCanvasElement | null;
  /** Which path produced the crop; ``none`` when both failed. */
  via: 'display-canvas' | 'rerender' | 'none';
  /** Diagnostic trail when ``canvas`` is null. */
  reason?: string;
};

/**
 * New Thumbnail for PDFs: blit the on-screen page when it is readable,
 * otherwise re-render a clean page from the junkdrawer blob URL. html2canvas
 * must not run on pdf.js canvases (tainted / hanging clone).
 */
export async function capturePdfRegionCanvasAsync(
  root: HTMLElement,
  cap: PdfRegionCapture,
): Promise<PdfRegionCaptureResult> {
  const blit = blitPdfRegion(root, cap);
  if (blit.canvas) return { canvas: blit.canvas, via: 'display-canvas' };
  const blitReason = `blit(${blit.reason ?? 'unknown'})`;

  const host = root.querySelector(`[${PDF_CAPTURE_URL_ATTR}]`);
  const fileUrl = host?.getAttribute(PDF_CAPTURE_URL_ATTR)?.trim() ?? '';
  if (!fileUrl) {
    return { canvas: null, via: 'none', reason: `${blitReason}, no-capture-url` };
  }
  if (cap.width <= 0 || cap.height <= 0) {
    return { canvas: null, via: 'none', reason: `${blitReason}, empty-selection` };
  }

  const display = findPdfPageDisplay(root);
  if (!display) {
    return { canvas: null, via: 'none', reason: `${blitReason}, no-page-box` };
  }

  const rotation = parseCaptureRotation(host);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const targetWidth = Math.max(
    1,
    display.canvas && display.canvas.width > 0
      ? display.canvas.width
      : Math.round(display.box.width * dpr),
  );

  let rendered: CropOutcome;
  try {
    rendered = await withTimeout(
      renderPdfPageCanvas({ fileUrl, rotation, targetWidth }),
      PDF_REGION_CAPTURE_TIMEOUT_MS,
      'render-timeout',
    );
  } catch (error) {
    return {
      canvas: null,
      via: 'none',
      reason: `${blitReason}, ${error instanceof Error ? error.message : String(error)}`,
    };
  }
  if (!rendered.canvas) {
    return { canvas: null, via: 'none', reason: `${blitReason}, ${rendered.reason}` };
  }

  const cropped = cropOutcome(rendered.canvas, display.box, root, cap);
  if (!cropped.canvas) {
    return {
      canvas: null,
      via: 'none',
      reason: `${blitReason}, rerender-crop(${cropped.reason ?? 'unknown'})`,
    };
  }
  return { canvas: cropped.canvas, via: 'rerender' };
}
